import { FC, useEffect, useState } from "react";

import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";

import {
  API_REGIONS,
  APP_OPTIONS,
  getCommunityAdmin,
  patchCommunityAdmin,
  SSO_PROVIDERS,
} from "@/api/communityAdmin";

// Edit the rubi_org directory/discovery fields (province, SSO slug/provider,
// services slug, show-on-map, enabled apps, map center) for an existing org.
// These drive the login dropdown, services map, and SSO discovery, and were
// previously only settable at onboarding. Self-contained and orgId-scoped;
// `onSaved` lets the wizard advance to the next step after a save.
export const OrgDirectoryPanel: FC<{ orgId: string; onSaved?: () => void }> = ({
  orgId,
  onSaved,
}) => {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const [shortTitle, setShortTitle] = useState("");
  const [apiRegion, setApiRegion] = useState("ca");
  const [province, setProvince] = useState("");
  const [regionLabel, setRegionLabel] = useState("");
  const [ssoSlug, setSsoSlug] = useState("");
  const [ssoProvider, setSsoProvider] = useState("");
  const [servicesUrlSlug, setServicesUrlSlug] = useState("");
  const [lat, setLat] = useState("");
  const [lng, setLng] = useState("");
  const [displayOnMap, setDisplayOnMap] = useState(true);
  const [apps, setApps] = useState<string[]>([]);
  const [operationalAreas, setOperationalAreas] = useState<string[]>([]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    getCommunityAdmin(orgId)
      .then((o) => {
        if (!active) return;
        setShortTitle(o.short_title ?? "");
        setApiRegion(o.api_region || "ca");
        setProvince(o.province ?? "");
        setRegionLabel(o.region_label ?? "");
        setSsoSlug(o.sso_slug ?? "");
        setSsoProvider(o.sso_provider ?? "");
        setServicesUrlSlug(o.services_url_slug ?? "");
        setDisplayOnMap(o.display_on_services_map);
        setApps(o.approved_apps ?? []);
        setOperationalAreas(o.operational_areas ?? []);
        setLat(o.map_center ? String(o.map_center.lat) : "");
        setLng(o.map_center ? String(o.map_center.lng) : "");
        setLoadError(null);
      })
      .catch((e) => {
        if (active)
          setLoadError(
            e instanceof Error ? e.message : "Failed to load community",
          );
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [orgId]);

  const handleSave = async () => {
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      const latNum = parseFloat(lat);
      const lngNum = parseFloat(lng);
      await patchCommunityAdmin(orgId, {
        short_title: shortTitle.trim() || null,
        api_region: apiRegion,
        province: province.trim() || null,
        region_label: regionLabel.trim() || null,
        sso_slug: ssoSlug.trim() || null,
        sso_provider: ssoProvider || null,
        services_url_slug: servicesUrlSlug.trim() || null,
        display_on_services_map: displayOnMap,
        approved_apps: apps,
        operational_areas: operationalAreas.length ? operationalAreas : null,
        map_center:
          Number.isFinite(latNum) && Number.isFinite(lngNum)
            ? { lat: latNum, lng: lngNum }
            : null,
      });
      setSaved(true);
      onSaved?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to save details");
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <Typography>Loading…</Typography>;

  return (
    <Stack spacing={2} sx={{ maxWidth: 560 }}>
      {loadError && <Alert severity="error">{loadError}</Alert>}
      {error && <Alert severity="error">{error}</Alert>}
      {saved && (
        <Alert severity="success" onClose={() => setSaved(false)}>
          Saved
        </Alert>
      )}
      <Typography variant="body2" color="text.secondary">
        Directory & publish settings. These drive the login dropdown, services
        map, and SSO discovery.
      </Typography>
      <TextField
        label="Short title"
        value={shortTitle}
        onChange={(e) => setShortTitle(e.target.value)}
      />
      <TextField
        select
        label="API region"
        value={apiRegion}
        onChange={(e) => setApiRegion(e.target.value)}
        helperText="Which regional stack the agency belongs to. Sets the citizen host for its services links (.ca or .com)."
      >
        {API_REGIONS.map((r) => (
          <MenuItem key={r.value} value={r.value}>
            {r.label}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        label="SSO slug (login route: /{slug}/login)"
        value={ssoSlug}
        onChange={(e) => setSsoSlug(e.target.value)}
      />
      <TextField
        select
        label="SSO provider"
        value={ssoProvider}
        onChange={(e) => setSsoProvider(e.target.value)}
      >
        <MenuItem value="">(none)</MenuItem>
        {SSO_PROVIDERS.map((p) => (
          <MenuItem key={p} value={p}>
            {p}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        label="Services URL slug (citizen app)"
        value={servicesUrlSlug}
        onChange={(e) => setServicesUrlSlug(e.target.value)}
      />
      <TextField
        label="Province / state (e.g. ON, BC)"
        value={province}
        onChange={(e) => setProvince(e.target.value)}
      />
      <TextField
        label="Region label (map grouping)"
        value={regionLabel}
        onChange={(e) => setRegionLabel(e.target.value)}
      />
      <Stack direction="row" spacing={2}>
        <TextField
          label="Map center latitude"
          value={lat}
          onChange={(e) => setLat(e.target.value)}
        />
        <TextField
          label="Map center longitude"
          value={lng}
          onChange={(e) => setLng(e.target.value)}
        />
      </Stack>
      <FormControlLabel
        control={
          <Switch
            checked={displayOnMap}
            onChange={(e) => setDisplayOnMap(e.target.checked)}
          />
        }
        label="Show on public services map"
      />
      <Autocomplete
        multiple
        freeSolo
        options={[]}
        value={operationalAreas}
        onChange={(_, value) => setOperationalAreas(value as string[])}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Alert operational areas (divisions)"
            helperText="Type a division name and press Enter to add it"
          />
        )}
      />
      <Box>
        <Typography variant="subtitle2">Enabled apps</Typography>
        {APP_OPTIONS.map((a) => (
          <FormControlLabel
            key={a.value}
            control={
              <Checkbox
                checked={apps.includes(a.value)}
                onChange={(e) =>
                  setApps((prev) =>
                    e.target.checked
                      ? [...prev, a.value]
                      : prev.filter((x) => x !== a.value),
                  )
                }
              />
            }
            label={a.label}
          />
        ))}
      </Box>
      <Box>
        <Button variant="contained" disabled={busy} onClick={handleSave}>
          {busy ? <CircularProgress size={20} /> : "Save"}
        </Button>
      </Box>
    </Stack>
  );
};

export default OrgDirectoryPanel;
